import React, { useState, useEffect, useContext, useRef } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { navigate } from '@reach/router';
import { goNextPage, isEditPage } from 'utils/ebUtils';
import { orgId, setPageTitle } from 'utils';
import { Formik } from 'formik';
import styled from 'styled-components';
import v from 'scss/v';
import * as signUpConstants from 'constants/signUp';
import * as additionalInformationSelectors from 'state/selectors/additionalInformationSelectors';
import * as additionalInformationActions from 'state/actions/additionalInformationActions';
import AdditionalInformationRow from 'components/forms/additional-information/additionalInformationRow';
import SkipButton from 'commons/SkipButton';
import SaveButton from 'commons/SaveButton';
import CancelButton from 'commons/CancelButton';
import { I18N, I18NContext, I18NConsumer, GlobalContext } from 'commons';
import ReactGA from 'react-ga';
import SignUpHeader from '../SignUpHeader';
import SignUpContent from '../SignUpContent';
import LocationformationCard from '../InformationCard';
import ErrorContainer from './ErrorContainer';
import AlertBox from 'components/forms/AlertBox'

const SaveButtonContainer = styled.div`
  display: flex;
  padding: 10px;
  justify-content: flex-end;

  @media only screen and (max-width: ${v('$small-screen')}px) {
    justify-content: center;
  }
`;

const AdditionalInformationSection = ({ customFields, getCustomFields, saveCustomFields, isEditMode }) => {
  const { account: { id: contactId }, organization: { orgName } } = useContext(GlobalContext);
  const i18n = useContext(I18NContext);
  const [showSkipBtn, setShowSkipBtn] = useState(true);
  const tipAdditionInfoText = 'ssp.additional.information.tip';
  const tipAdditionInfoTitle = 'new.registration.signup.create.account.label.addtionalInformation';
  const isNewEditPage = isEditPage('edit-additionalInformation');
  const [isSavedSucceed, setSavedSucceed] = useState(false)

  useEffect(() => {
    ReactGA.pageview(`/newRegistration/informationPage/${orgId}`);
    getCustomFields(contactId);
  }, []);

  useEffect(() => {
    if (_.some(customFields, ['mandatory', true])) {
      setShowSkipBtn(false);
    }
  }, [customFields]);

  return (
    <div className="sign-up container ssp-well">
      <I18NConsumer>
        {() => setPageTitle(`${orgName} - ${i18n['ssp.register.additional.information.title']}`)}
      </I18NConsumer>
      {
        !isEditMode && <SignUpHeader currentSection={signUpConstants.SIGN_UP_SECTIONS.information.id} />
      }
      <I18N
        tag={SignUpContent}
        isInLoggedInEditProfileMode={isEditMode}
        i18n-title={tipAdditionInfoTitle}
      >
        <div className="form-group row pt-4 p-2">
          <I18N
            tag={LocationformationCard}
            className="col-12"
            i18n-tipTitle={tipAdditionInfoTitle}
            i18n-tipText={tipAdditionInfoText}
            i18n-instruction={'form.fields.are.required'}
          />
          <Formik
            initialValues={Object.assign(
              {},
              ...customFields.map(({ attrId, savedValues }) => ({ [attrId]: savedValues || '' })))
            }
            enableReinitialize
            onSubmit={(values) => {
              customFields.map((item) => {
                item.savedValues = values[item.attrId] instanceof Array ? values[item.attrId] : [values[item.attrId]];
              });
              saveCustomFields(
                contactId,
                customFields,
                () => {
                  if (isEditMode) {
                    setSavedSucceed(true)
                  } else {
                    goNextPage({ pageUri: 'edit-additionalInformation', contactId });
                  }
                });
            }}
          >
            {({
              values,
              touched,
              errors,
              setFieldValue,
              setFieldTouched,
              setFieldError,
              submitForm,
              isSubmitting,
            }) => (
              <div className="col-md-8" role="form">
                <AlertBox message='home.alert.box.message.information' isSavedSucceed={isSavedSucceed} />
                <ErrorContainer isSubmitting={isSubmitting} errors={errors} />
                {customFields.map((customField, index) => (
                  <AdditionalInformationRow
                    customField={customField}
                    key={`${customField.attrId}`}
                    index={index}
                    {...{
                      values, touched, errors, setFieldValue, setFieldTouched, setFieldError
                    }}
                  />
                ))}
                {!isNewEditPage && !isEditMode &&
                      <div className="offset-md-3 col-md-9 pr-0">
                        <div className="row">
                          <SkipButton
                            isShow={showSkipBtn}
                            onClick={() => {
                              goNextPage({ pageUri: 'edit-additionalInformation', contactId });
                            }}
                            ebTestValue="subscription-skip"
                          />
                          <SaveButton
                            onClick={submitForm}
                            ebTestValue="ssp-additionalInformation-button-save"
                            type="button"
                          />
                        </div>
                      </div>
                }
                {
                  isNewEditPage && !isEditMode &&
                      <div className="offset-md-3 col-md-9 pr-0">
                        <div className="row">
                          <CancelButton
                            isShow="true"
                            onClick={() => {
                              goNextPage({ pageUri: 'edit-additionalInformation', contactId });
                            }}
                            ebTestValue="ssp_additionalInformation_button_cancel"
                          />
                          <SaveButton
                            onClick={submitForm}
                            ebTestValue="ssp_additionalInformation_button_save"
                            type="button"
                            hasContinue={false}
                          />
                        </div>
                      </div>
                }
                {
                  isEditMode &&
                  <SaveButtonContainer className="row mt-2">
                    <CancelButton
                      isShow="true"
                      onClick={() => {
                        navigate('./home');
                      }}
                      ebTestValue="ssp_myprofile_button_cancel"
                    />
                    <SaveButton
                      onClick={() => {setSavedSucceed(false); submitForm()}}
                      ebTestValue="ssp_myprofile_button_submit"
                      type="submit"
                      hasContinue={false}
                    />
                  </SaveButtonContainer>
                }
              </div>
            )}
          </Formik>
        </div>
      </I18N>
    </div>
  );
};

const mapStateToProps = state => ({
  customFields: additionalInformationSelectors.selectCustomFields(state),
});

const mapDispatchToProps = dispatch => ({
  getCustomFields: (contactId) =>
    dispatch(additionalInformationActions.getCustomFields.action({ contactId })),
  saveCustomFields: (contactId, customFields, successCallback, failCallback) =>
    dispatch(additionalInformationActions.saveCustomFields.action({
      contactId, customFields, successCallback, failCallback
    }),
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalInformationSection);
